// Presentation of offers the BACKEND has already decided. Nothing here decides
// whether an offer applies, which one wins, or how much it takes off: the
// catalogue projection (price / offer / discount fields, decorated per
// channel and outlet by decorateItemsWithOutletOffers) and the cart quote
// (items[].appliedOffer, appliedCartOffer) are the only authorities. These
// helpers pick which of the returned numbers to show and word them.

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const positive = (value) => {
  const number = toNumber(value);
  return number !== null && number > 0 ? number : null;
};

export const formatRupees = (value) => {
  const number = toNumber(value);
  if (number === null) return null;
  return `₹${Number.isInteger(number) ? number : number.toFixed(2)}`;
};

// Offer dates are shown in the store's own time zone, whatever the browser's.
export const formatOfferDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
};

/**
 * How to show one product projection's price.
 *
 *   price               the price the backend says the customer pays here
 *   compareAtPrice      struck-through reference, only when genuinely higher:
 *                       the MRP, or — for a backend-applied offer with no MRP
 *                       above it — the pre-offer price (`networkPrice`) the
 *                       backend itself measured the discount against
 *   discountPercentage  the backend's own "% off", when positive
 *   savings             the backend's own rupees off, alongside a struck price
 *   offer               the offer the backend applied to this price, if any
 *   basketOffer         a multi-buy offer the backend attached for display;
 *                       it does not change the browse price
 */
export const productPriceView = (product) => {
  const source = product || {};
  const price = toNumber(source.price);
  const mrp = positive(source.mrp);
  const networkPrice = positive(source.networkPrice);
  const appliedOffer = source.offer && (source.offer.label || source.offer.name) ? source.offer : null;

  let compareAtPrice = null;
  if (price !== null) {
    if (mrp !== null && mrp > price) compareAtPrice = mrp;
    else if (appliedOffer && networkPrice !== null && networkPrice > price) compareAtPrice = networkPrice;
  }

  const percentage = positive(source.discountPercentage);
  const amount = positive(source.discountAmount);

  return {
    price,
    compareAtPrice,
    discountPercentage: percentage === null ? null : Math.round(percentage),
    savings: compareAtPrice !== null ? amount : null,
    offer: appliedOffer
      ? { label: appliedOffer.label || appliedOffer.name, endsAt: appliedOffer.endsAt || null }
      : null,
    basketOffer: source.basketOffer?.label
      ? { label: source.basketOffer.label, minQuantity: positive(source.basketOffer.minQuantity) }
      : null,
  };
};

// ── GET /offers entries (utils/customerOffer.js publicOffer) ────────────────

/** The benefit in words, from the offer's own benefit fields. */
export const offerBenefitText = (offer) => {
  const benefit = offer?.benefit || {};
  const perUnit = offer?.offerType === 'product_percent' || offer?.offerType === 'product_flat';
  const each = perUnit ? ' each' : '';
  const percent = positive(benefit.percent);
  const cap = positive(benefit.maxDiscount);
  const amount = positive(benefit.amount);
  switch (benefit.type) {
    case 'percent':
      if (percent === null) return null;
      return cap !== null ? `${percent}% off, up to ${formatRupees(cap)}${each}` : `${percent}% off${each}`;
    case 'flat':
      return amount !== null ? `${formatRupees(amount)} off${each}` : null;
    case 'free_delivery':
      return 'Free delivery';
    case 'free_items': {
      const free = positive(benefit.freeQuantity);
      return free !== null ? `${free} item${free === 1 ? '' : 's'} free` : 'Free items';
    }
    default:
      return null;
  }
};

/** The offer's stated conditions, as short customer-facing phrases. */
export const offerConditionTexts = (offer) => {
  if (!offer) return [];
  const texts = [];
  const minQuantity = positive(offer.condition?.minQuantity);
  if (minQuantity !== null) texts.push(`Buy ${minQuantity} or more`);
  const minOrder = positive(offer.minOrderValue);
  if (minOrder !== null) texts.push(`On orders of ${formatRupees(minOrder)} or more`);

  const appliesTo = offer.appliesTo || {};
  const labels = [...(appliesTo.categories || []), ...(appliesTo.subCategories || [])]
    .map((entry) => entry?.label)
    .filter(Boolean);
  const productCount = positive(appliesTo.productCount);
  if (labels.length > 0) texts.push(`On ${labels.join(', ')}`);
  if (productCount !== null) texts.push(`On ${productCount} selected product${productCount === 1 ? '' : 's'}`);

  if (offer.customerEligibility === 'first_order') texts.push('First order only');
  return texts;
};
